// This is a conainer component that renders appropriate chart control component based on user selection

import ChartColors from './Color/ChartColors';
import ColorScale from './Color/ColorScale';
import ColorSteps from './Color/ColorSteps';
import SankeyColorControls from './Color/SankeyColorControls';
import ChartFormat from './Format/ChartFormat';
import AxisControls from './GridAndAxes/AxisControls';
import GridAndAxes from './GridAndAxes/GridAndAxes';
import CalendarLabels from './Labels/CalendarLabels';
import ChartLabels from './Labels/ChartLabels';
import ChartLegend from './Legend/ChartLegend';
import TreeMapLegend from './Legend/TreeMapLegend';
import ChartMargin from './Margin/ChartMargin';
import BoxPlotChartStyles from './ChartStyle/BoxPlotChartStyles';
import FilledMapStyles from './ChartStyle/GeoChartStyles/FilledMapStyles';
import BubbleMapStyles from './ChartStyle/GeoChartStyles/BubbleMapStyles';
import CalendarChartStyles from './ChartStyle/CalendarChartStyles';
import ChartStyle from './ChartStyle/ChartStyle';
import SankeyStyles from './ChartStyle/SankeyStyles';
import TreeMapStyles from './ChartStyle/TreeMapStyles';
import ChartTitle from './Title/ChartTitle';
import CardStyle from './ChartStyle/CardStyle';
import TitleForDynamicMeasures from './Title/TitleForDynamicMeasures';
import ChartFormatForDm from './Format/ChartFormatForDm';
import DynamicMeasureStyle from './ChartStyle/DynamicMeasureStyle';
import WordCloudStyles from './ChartStyle/WordCloudStyles';
import WordCloudIgnoreWords from './WordCloudIgnoreWords';
import TableConditionalFormating from './TableChartControlComponents/TableConditionalFormatting';
import DynamicMeasureConditionalFormattingComponent from './DynamicMeasureConditionalFormattingComponent';
import SimplecardConditionalFormatting from './SimplecardConditionalFormatting';
import Sort from './Sort/Sort';
import ShowHide from './ShowHide/ShowHide';
import BubbleMapColors from './Color/BubbleMapColors';
import LineChartStyles from './ChartStyle/LineChartStyles';
import SimpleCardLabel from './Labels/SimpleCardLabel';
import SimpleCardColors from './Color/SimpleCardColors';
import FilledMapColors from './Color/FilledMapColors';
import TotalSettings from './Total/Total';
import Security from './RowLevelSecurity/Security';
import TooltipFormat from './Format/TooltipFormat';
import BarLineSettings from './BarLine/BarLineSettings';
import { useDispatch } from 'react-redux';
import { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../redux';
import Grid from './GridAndAxes/Grid';
import Dataset from './Dataset/Dataset';
import CircularColorControls from './Color/CircularColorControls';
import SeriesColorControls from './Color/SeriesColorControls';
import ScatterColorControls from './Color/ScatterColorControls';
import FunnelColorControls from './Color/FunnelColorControls';
import TreemapColorControls from './Color/TreemapColorControls';
import BarColorControls from './Color/BarColorControls';
import WaterfallColorControls from './Color/WaterfallColorControls';
import TableLayout from './Layout/TableLayout';
import ScatterStyles from './ScatterStyles';
import VisualScaleSeparator from './ChartStyle/GeoChartStyles/visualScaleSeparator';

interface ControlDetailProps {
  fromDashboard?: boolean;
  iconColor?: string;
  backgroundColor?: string;
}

const ControlDetail: React.FC<ControlDetailProps> = ({
  fromDashboard = false,
  iconColor,
  backgroundColor,
}) => {
  const { chartProperties, tabTileProps, dynamicMeasureState } = useSelector((state: RootState) => {
    return {
      chartProperties: state.chartProperties,
      tabTileProps: state.tabTileProps,
      dynamicMeasureState: state.dynamicMeasuresState,
    };
  });

  var propKey: string = `${tabTileProps.selectedTabId}.${tabTileProps.selectedTileId}`;

  var chartType: string = useMemo(
    () => chartProperties.properties[propKey].chartType,
    [chartProperties.properties[propKey].chartType],
  );

  var chartOption = useMemo(() => {
    if (chartType === 'richText') {
      return dynamicMeasureState.dynamicMeasureProps?.[tabTileProps.selectedTabId]?.[
        tabTileProps.selectedTileId
      ]?.[`${tabTileProps.selectedTileId}.${dynamicMeasureState.selectedDynamicMeasureId}`]
        ?.chartOptionSelected;
    } else {
      return chartProperties.properties[propKey].chartOptionSelected;
    }
  }, [chartType, chartProperties.properties[propKey].chartOptionSelected]);
  const dispatch = useDispatch();

  const RenderControlDetail = useCallback(() => {
    switch (chartOption) {
      case 'Title':
        if (chartType === 'richText') {
          return <TitleForDynamicMeasures />;
        } else {
          return <ChartTitle fromDashboard={fromDashboard} iconColor={iconColor} />;
        }
      case 'Dataset':
        return <Dataset Color={iconColor} />;

      case 'Color':
        if (chartType === 'heatmap' || chartType === 'calendar') {
          return <ColorScale iconColor={iconColor} />;
        } else if (chartType === 'gauge') {
          return <ColorSteps iconColor={iconColor} />;
        } else if (chartType === 'sankey') {
          return <SankeyColorControls iconColor={iconColor} />;
        } else if (chartType === 'pie' || chartType === 'donut' || chartType === 'rose') {
          return <CircularColorControls iconColor={iconColor} />;
        } else if (chartType === 'line' || chartType === 'area' || chartType === 'stackedArea') {
          return <SeriesColorControls iconColor={iconColor} />;
        } else if (chartType === 'funnel') {
          return <FunnelColorControls iconColor={iconColor} />;
        } else if (chartType === 'scatterPlot') {
          return <ScatterColorControls iconColor={iconColor} />;
        } else if (chartType === 'treeMap') {
          return <TreemapColorControls iconColor={iconColor} />;
        } else if (
          chartType === 'multibar' ||
          chartType === 'horizontalBar' ||
          chartType === 'stackedBar' ||
          chartType === 'horizontalStacked'
        ) {
          return <BarColorControls iconColor={iconColor} />;
        } else if (chartType === 'waterfall') {
          return <WaterfallColorControls iconColor={iconColor} />;
        } else if (chartType === 'bubbleMap') {
          return <BubbleMapColors iconColor={iconColor} />;
        } else if (chartType === 'filledMap') {
          return <FilledMapColors iconColor={iconColor} />;
        } else if (chartType === 'simplecard') {
          return <SimpleCardColors iconColor={iconColor} />;
        } else {
          return <ChartColors iconColor={iconColor} from={fromDashboard ? 'dashboard' : ''} />;
        }
      case 'Legend':
        if (chartType === 'treeMap') {
          return <TreeMapLegend iconColor={iconColor} />;
        } else {
          return <ChartLegend iconColor={iconColor} />;
        }
      case 'Margin':
        return <ChartMargin iconColor={iconColor} />;
      case 'Tooltip':
        return <TooltipFormat iconColor={iconColor} />;
      case 'Grid':
        return <Grid iconColor={iconColor} />;
      case 'Axis':
        if (
          chartType === 'multibar' ||
          chartType === 'stackedBar' ||
          chartType === 'horizontalBar' ||
          chartType === 'horizontalStacked' ||
          chartType === 'line' ||
          chartType === 'area' ||
          chartType === 'stackedArea' ||
          chartType === 'scatterPlot' ||
          chartType === 'boxPlot' ||
          chartType === 'heatmap' ||
          chartType === 'waterfall'
        ) {
          return <GridAndAxes iconColor={iconColor} backgroundColor={backgroundColor} />;
        } else {
          return <AxisControls iconColor={iconColor} backgroundColor={backgroundColor} />;
        }
      case 'Label':
        if (chartType === 'simplecard') {
          return <SimpleCardLabel iconColor={iconColor} backgroundColor={backgroundColor} />;
        } else if (chartType === 'calendar') {
          return <CalendarLabels iconColor={iconColor} backgroundColor={backgroundColor} />;
        } else {
          return <ChartLabels iconColor={iconColor} backgroundColor={backgroundColor} />;
        }
      case 'Rotation':
        return <AxisControls iconColor={iconColor} backgroundColor={backgroundColor} />;
      case 'Style':
        if (chartType === 'calendar') {
          return <CalendarChartStyles iconColor={iconColor} />;
        } else if (chartType === 'boxPlot') {
          return <BoxPlotChartStyles />;
        } else if (chartType === 'treeMap') {
          return <TreeMapStyles iconColor={iconColor} />;
        } else if (chartType === 'sankey') {
          return <SankeyStyles iconColor={iconColor} backgroundColor={backgroundColor} />;
        } else if (chartType === 'simplecard') {
          return <CardStyle iconColor={iconColor} fromDashboard={fromDashboard} />;
        } else if (chartType === 'richText') {
          return <DynamicMeasureStyle />;
        } else if (chartType === 'wordCloud') {
          return <WordCloudStyles iconColor={iconColor} />;
        } else if (chartType === 'scatterPlot') {
          return <ScatterStyles iconColor={iconColor} />;
        } else if (chartType === 'filledMap') {
          return (
            <div>
              <FilledMapStyles iconColor={iconColor} />
            </div>
          );
        } else if (chartType === 'bubbleMap') {
          return (
            <div>
              <BubbleMapStyles iconColor={iconColor} />
            </div>
          );
        } else if (chartType === 'line' || chartType === 'area' || chartType === 'stackedArea') {
          return <LineChartStyles iconColor={iconColor} />;
        } else {
          return <ChartStyle iconColor={iconColor} />;
        }
      case 'Format':
        if (chartType === 'richText') {
          return <ChartFormatForDm />;
        }
        if (
          chartType === 'bar' ||
          chartType === 'line' ||
          chartType === 'area' ||
          chartType === 'stackedArea' ||
          chartType === 'calendar' ||
          chartType === 'heatmap' ||
          chartType === 'sankey' ||
          chartType === 'treeMap' ||
          chartType === 'pie' ||
          chartType === 'donut' ||
          chartType === 'rose'
        ) {
          return null;
        }

        return (
          <ChartFormat
            chartType={chartType}
            iconColor={iconColor}
            backgroundColor={backgroundColor}
          />
        );

      case 'Cond.Form':
        //   return <TableConditionalFormating />;
        // case "Conditional Formatting":
        if (chartType === 'richText') {
          return (
            <DynamicMeasureConditionalFormattingComponent
              iconColor={iconColor}
              backgroundColor={backgroundColor}
            />
          );
        } else if (chartType === 'simplecard') {
          return (
            <SimplecardConditionalFormatting
              iconColor={iconColor}
              backgroundColor={backgroundColor}
            />
          );
        } else {
          return (
            <TableConditionalFormating iconColor={iconColor} backgroundColor={backgroundColor} />
          );
        }
      case 'Sort':
        if (chartType === 'crossTab') {
          return <Sort iconColor={iconColor} />;
        } else {
          return <Sort iconColor={iconColor} />;
        }
      case 'Show/Hide':
        return <ShowHide iconColor={iconColor} backgroundColor={backgroundColor} />;
      case 'Ignore words':
        return <WordCloudIgnoreWords iconColor={iconColor} />;
      case 'Total':
        return <TotalSettings iconColor={iconColor} />;
      case 'Bar/Line':
        return <BarLineSettings iconColor={iconColor} />;
      case 'User Level Filter':
        return <Security />;
      case 'Layout':
        return <TableLayout iconColor={iconColor} />;
      case 'Visual Scale':
        return <VisualScaleSeparator iconColor={iconColor} backgroundColor={backgroundColor} />;

      default:
        return (
          <span>
            {chartProperties.properties[propKey].chartOptionSelected} properties Under Construction
          </span>
        );
    }
  }, [chartOption]);

  return <RenderControlDetail />;
};

export default ControlDetail;
